import { isPreproduction, isProduction } from '../../../env'

/** Helper for getting only smProducts referenced by a pgPage */
export const referencedByPgPageCondition = `count(*[_type == "pgPage" && references(^._id)]) > 0`

/** Helper for getting directly SVG files. **DO NOT USE WITH IMAGES**. There's a builder for constructing optimized images based on its reference */
export const svgAsset = '{...image{alt,"src":asset->url}}'

export const iconAsset = '{...icon{alt,"src":asset->url}}'

/** Full query for a link. Not for usage within layout definition */
export const atLink = `{...,leftIcon->${iconAsset},rightIcon->${iconAsset}}`

export const atNavbarLink = '{href,label,target}'

export const mlNavbarDropdown = `{title, dropdownLinks[]->${atNavbarLink}}`

export const atButton = `{label,actionValue,actionType,variant,target,leftIcon->${iconAsset},rightIcon->${iconAsset}}`

export const imageAsset =
  '{...image{caption,asset,"src":asset->url,"dimensions":asset->metadata.dimensions,"type":_type,alt}}'
export const mediaAsset = `{
  _type,
  _id,
  "type": select(
    _type == "atImage" => "image",
    _type == "smVideoFile" => "video",
    _type == "smModel3d" => "model3d"
  ),
  ...select(
    _type == "atImage" => {
      "caption": image.caption,
      "alt": image.alt,
      "src": image.asset->url,
      "dimensions": image.asset->metadata.dimensions,
      "asset": image.asset
    },
    _type == "smVideoFile" => {
      name,
      "poster": poster->image.asset->url,
      loop,
      autoPlay,
      videoControls,
      "src": file.asset->url
    },
    _type == "smModel3d" => {
      name,
      autoRotate,
      "src": file.asset->url
    }
  )
}`

export const mlNotification = '{title,message,notificationType,linkLabel}'

export const mlSnackbar = '{content,buttonLabel,status}'

export const iconInformativeList = `items[]{title,subtitle,body,icon->${iconAsset}}`

export const nonBookableDates = `{
  name,
  dates[]{
    "startDate": startDate,
    "endDate": endDate
  }
}`

// TODO: Cards layout is missing expansion
export const orFlexibleContent = `
{
  contentTitle,
  layout,
  layout == "flexibleA" => {
    description,
    images[]->${imageAsset},
    "details": details->${iconInformativeList},
    shortDescription
  },
  layout == "flexibleB" => {
    description,
    images[]->${imageAsset},
    detailsTitle,
    "directions": directions->directions,
    shortDescription
  },
  layout == "flexibleC" => {
    address,
    "details": details->${iconInformativeList},
    description
  },
  layout == "flexibleD" => {
    description,
    images[]->${imageAsset},
    detailsTitle,
    detailsBody,
    "details": details->${iconInformativeList},
    shortDescription
  },
  layout == "cardA" => {
    cards
  }
}
`

export const orContainerCtaBar = `
  {
    "type": "ctaBar",
    label,
    rightIcon->${iconAsset},
    actionValue,
    target,
    secondaryLabel,
    secondaryRightIcon->${iconAsset},
    secondaryActionValue,
    secondaryTarget,
  }
`

export const smVideoFileRef = `{
  _id,
  "type": "video",
  name,
  "poster": poster->image.asset->url,
  loop,
  autoPlay,
  videoControls,
  "src": file.asset->url
}`

export const videoFileABTestingConfig = `{
  active,
  cookieName,
  variants[]{
    variantName,
    trafficAllocation,
    videoFile->${smVideoFileRef}
  }
}`

// TODO: Missing calls for layouts other than `simpleTextList`
export const orBaseContent = `
{
  "type": "orBaseContent",
  layout,
  images[]->${imageAsset},
  media[]->${mediaAsset},
  videoABTestingConfig->${videoFileABTestingConfig},
  sectionTitle,
  sectionDescription,
  button->${atButton},
  sectionBody,
  layout == "simpleTextList" => {
    "list": list->items
  },
  layout == "longText" => {
    sectionFooter
  },
  layout == "descriptions" => {
    "list": list->items[]{title,body,link->${atLink}},
    orientation,
    splitContent,
    background,
  },
  layout == "finalAddendum" => {
    outline,
    sectionSubtitle,
    handwrittenDetail,
    outline2,
    sectionTitle2,
    sectionDescription2,
    orientation,
    splitContent,
  },
  layout == "featureList" => {
    "list": list->${iconInformativeList},
  },
  layout == "paragraphBody" => {
    orientation,
  },
  layout == "fetureListWithColumns" => {
    "list": list->${iconInformativeList},
  },
  layout == "paragraphBody-d" => {
    ctaBar->,
  }
}
`

export const atFormField = `
{
  inputType == 'grid' => {inputType,label,grid[]->{...,icon->${iconAsset}}},
  inputType != 'grid' => {...,icon->${iconAsset}}
}`

export const orTabs = (content: string, contentFilter = '') => `
{
  tabsTitle,
  name,
  fieldName,
  tabs[]->{
    icon->${iconAsset},
    label,
    content[${contentFilter}]->${content}
  }
}`

export const productId = isProduction ? 'prodPlatformId' : 'platformId'

export const smProductCard = `
{
  _id,
  spaceABTestingConfig->{
    active,
    "cookieName": cookieName.current,
    spaceVariants[]{
      trafficAllocation,
      variantName,
      spaceDetails[] {
        "key": _key,
        productName,
        inquiryOnly,
        priceRate,
        inquiryPrice,
        rateType,
        textAfterPrice,
        specialProductDetails[] {
          detail,
          "icon": icon->${iconAsset}
        }
      }
    }
  },
  plpRank,
  capacity,
  stripePrice,
  prodStripePrice,
  spaceType,
  productName,
  bookableDays,
  specialProductDetail{detail,icon->${iconAsset}},
  specialProductDetails[]{detail,icon->${iconAsset}},
  source,
  slug,
  "building": buildingRef->slug.current,
  "platformId": ${productId},
  images[]->${imageAsset},
  media[]->${mediaAsset},
  inquiryOnly,
  inquiryCurrency,
  inquiryPrice,
  textBeforePrice,
  rateType,
  textAfterPrice,
  "buildingBusinessHours": buildingRef->buildingBusinessHours->businessHours[0],
  spaceBusinessHours -> { ...businessHours[0]},
}`

export const smWorkspaceCard = `{spaceType,images[]->${imageAsset}}`

export const orContainerTextBlock = `
  {
    "type": "textBlock",
    content[]{
      _type == 'block' => {
        ...,
        children[]{
          ...,
          _type == 'imageReference' => {
            "image": @->.image
          }
        }
      }
    }
  }
`

export const orContainerForm = `
  {
    "type": "form",
    submissionType,
    submitButton->,
    title[],
    formId,
    isGate,
    gateRevealsContentAfter,
    "gateAttachedFileUrl": gateAttachedFile.asset->url,
    sendGatedContentByEmail,
    successSnackbar->,
    serverErrorNotification->,
    singleFieldErrorNotification->,
    multipleFieldsErrorNotification->,
    fields[]->{
      _type == 'atFormField' => ${atFormField},
      _type == 'orTabs' => ${orTabs(atFormField)}
    }
  }
`

export const mlSiteBanner = `
{
  "type": "mlSiteBanner",
  name,
  description,
  headline,
  variant,
  callToAction->${atLink},
  image->${imageAsset},
  media[]->${mediaAsset},
  mediaDesktop[]->${mediaAsset},
  logo->${imageAsset},
  alignment,
  orientation,
  defaultSolutionKey,
  solutions[]{solutionKey, label, description, buttonLabel}
}
`

const buildingDetailsCard = `
  {
    "buildingImages": buildingImages[0]->${mediaAsset},
    "building": buildingRef->slug.current,
    "page": *[_type == "pgPage" && template._ref == ^._id][0]{
      "name": title,
      "url": slug,
    },
    "spaceRates": spacesSection.spaceRates,
    "address": address.addressInfo
  }`

const mlCustomCard = `
  {
    name,
    title,
    description,
    image->${imageAsset},
    ctaBar->${orContainerCtaBar}
  }`

//TODO: fix the fetch of "products": to only receive those that are referenced by one or more pgPages
export const orContainerCarousel = `
  {
    "type": "carousel",
    "title": Title,
    type == "tm-building-detail" => {
      "carouselType": "building",
      "buildings": blocks[@->._type == "tmBuildingDetail"]->${buildingDetailsCard}
    },
    type == "sm-product" => {
      "carouselType": "product",
      "products": blocks[@->._type == "smProduct" && !@->privateSpace${
        isProduction || isPreproduction ? ' && @->publishSpace == true' : ''
      } && count(*[_type == "pgPage" && template._ref == @->._id]) > 0]->${smProductCard}
    },
    type == "ml-custom-card" => {
      "carouselType": "customCard",
      "customCards": blocks[@->._type == "mlCustomCard"]->${mlCustomCard}
    },
  }`

export const orMapTile = `
  {
    "type": "orMapTile",
    location,
    address,
    building -> { _type == 'tmBuildingDetail' => {
      "page": *[_type == "pgPage" && template._ref == ^._id][0]{
        "name": title,
        "url": slug,
      },
      buildingImages[]->${imageAsset},
      address,
      spacesSection {
        allSpacesButton -> ${atButton}
      }
    }},
    details -> {
      name,
      items
    }
  }
`

export const orMap = `
  {
    "type": "orMap",
    title,
    description,
    Name,
    Locations[]->{
      _type == 'orMapTile' => ${orMapTile}
    }
  }
`

export const orContainerSpaces = `
  {
    "type": "orSpaces",
    title,
    spaceType,
    hideSpaceTypeFilter,
    "region": coalesce(cityRef->slug.current, buildingRef->city->slug.current),
    "building": buildingRef->slug.current,
    disableRegionFilter,
    disableBuildingFilter,
  }
`

export const orContainer = `
  {
    "type": "orContainer",
    containerTitle,
    description,
    blocks[]->{
      _type == 'smTextBlock' => ${orContainerTextBlock},
      _type == 'mlCtaBar' => ${orContainerCtaBar},
      _type == 'orForm' => ${orContainerForm},
      _type == 'mlSiteBanner' => ${mlSiteBanner},
      _type == 'orCarousel' => ${orContainerCarousel},
      _type == 'orMap' => ${orMap},
      _type == 'orBaseContent' => ${orBaseContent},
      _type == 'orSpaces' => ${orContainerSpaces},
    },
  }
`

export const orContainerTwoCol = `
  {
    "type": "orContainerTwoCol",
    columnRatio,
    blocks[]->{
      _type == 'smTextBlock' => ${orContainerTextBlock},
      _type == 'mlCtaBar' => ${orContainerCtaBar},
      _type == 'orForm' => ${orContainerForm},
      _type == 'mlSiteBanner' => ${mlSiteBanner},
      _type == 'orCarousel' => ${orContainerCarousel},
      _type == 'orMap' => ${orMap},
      _type == 'orBaseContent' => ${orBaseContent},
      _type == 'orSpaces' => ${orContainerSpaces},
    },
  }
`
